import axios from 'axios'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { CRYPTO_ENABLED, CRYPTO_REQUEST_HEADER, encrypt, decrypt } from './crypto'

NProgress.configure({ showSpinner: false })

export interface ApiResponse<T = unknown> {
  success: boolean
  code: number
  message: string
  data: T | null
  timestamp: number
}

/** 加密请求体的包装格式 */
interface EncryptedBody {
  _encrypted: true
  payload: string
}

const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.jannyjanny.cn/api',
  timeout: 15000,
})

// ——— 请求拦截器 1：注入 Auth Token + NProgress ———
request.interceptors.request.use((config) => {
  NProgress.start()
  const token = localStorage.getItem('access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// ——— 请求拦截器 2：按前端配置声明并加密请求体 ———
request.interceptors.request.use(async (config) => {
  const method = (config.method || '').toUpperCase()
  const hasBody = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)

  if (CRYPTO_ENABLED) {
    config.headers[CRYPTO_REQUEST_HEADER] = 'true'
  }

  if (CRYPTO_ENABLED && hasBody && config.data != null) {
    const payload = await encrypt(config.data)
    config.data = { _encrypted: true, payload } satisfies EncryptedBody
  }
  return config
})

// ——— 响应拦截器 1：Prod 解密响应体 ———
request.interceptors.response.use(async (res) => {
  if (CRYPTO_ENABLED && res.data?._encrypted && typeof res.data.payload === 'string') {
    res.data = await decrypt<ApiResponse>(res.data.payload)
  }
  return res
})

// ——— 响应拦截器 2：业务层处理 + Token 刷新 ———
request.interceptors.response.use(
  (res) => {
    NProgress.done()
    return res
  },
  async (error) => {
    NProgress.done()
    // 错误响应体同样可能被加密，需先解密再处理
    if (CRYPTO_ENABLED && error.response?.data?._encrypted && typeof error.response.data.payload === 'string') {
      try {
        error.response.data = await decrypt<ApiResponse>(error.response.data.payload)
      } catch {
        // 解密失败时保留原始数据
      }
    }
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          const refreshBody = CRYPTO_ENABLED
            ? ({ _encrypted: true, payload: await encrypt({ refreshToken }) } satisfies EncryptedBody)
            : { refreshToken }
          const res = await axios.post(
            `${import.meta.env.VITE_API_BASE_URL || '/api'}/auth/refresh`,
            refreshBody,
            {
              headers: CRYPTO_ENABLED ? { [CRYPTO_REQUEST_HEADER]: 'true' } : undefined,
            },
          )
          const data =
            CRYPTO_ENABLED && res.data?._encrypted && typeof res.data.payload === 'string'
              ? await decrypt<ApiResponse<{ accessToken: string }>>(res.data.payload)
              : (res.data as ApiResponse<{ accessToken: string }>)
          const newToken = data.data?.accessToken
          if (newToken) {
            localStorage.setItem('access_token', newToken)
            error.config.headers.Authorization = `Bearer ${newToken}`
            return request(error.config)
          }
        } catch {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/auth/login'
        }
      } else {
        window.location.href = '/auth/login'
      }
    }
    return Promise.reject(error)
  },
)

export default request
